import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Table from '@src/new-components/Table';
import Typography from '@src/new-components/Typography';
import CopyButton from '@src/new-components/Button/CopyButton';
import NoDataImage from '@src/assets/images/new-no-data.svg';
import { formatRewardCode } from '@src/services/topPaidEvent';
import { COLOR } from '@src/styles/color';
import { StyledRewardCodes } from './index.style';

const RECEIVED_AT_FORMAT = 'DD/MM/YYYY - HH:mm';
// The theme adds a 1px border on hover; with the outlined padding that grew the button and the row.
const COPY_BUTTON_STYLE = {
  padding: 0,
  border: 'none',
  backgroundColor: 'transparent',
  '&:hover': { border: 'none', backgroundColor: COLOR.indigo[4] },
};

const CodeValue = ({ code }) => (
  <span className="code-cell">
    <Typography variant="body-16-medium">{formatRewardCode(code)}</Typography>
    <CopyButton
      content={formatRewardCode(code)}
      size={24}
      iconCopy={<ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
      iconCopied={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
      customStyle={COPY_BUTTON_STYLE}
    />
  </span>
);

const RewardCodeList = forwardRef(({ codes }, ref) => {
  const { t } = useTranslation();

  const rows = codes.map((item, index) => ({
    ...item,
    id: `${item.code}-${index}`,
    receivedAtText: moment(item.createdAt).format(RECEIVED_AT_FORMAT),
  }));

  const columns = [
    {
      field: 'code',
      title: t('topPaidEventRewardCodes'),
      align: 'center',
      render: (row) => <CodeValue code={row.code} />,
    },
    {
      field: 'receivedAtText',
      title: t('topPaidEventReceivedAt'),
      align: 'center',
    },
  ];

  return (
    <StyledRewardCodes ref={ref}>
      <div className="codes-heading">
        <div className="icon-box">
          <ConfirmationNumberOutlinedIcon fontSize="small" />
        </div>
        <Typography
          variant="body-24-semibold"
          component="h2"
          className="section-title"
        >
          {t('topPaidEventMyCodes')}
        </Typography>
      </div>

      {rows.length === 0 ? (
        <div className="empty-codes">
          <img src={NoDataImage} alt="" />
          <Typography variant="body-14-regular">
            {t('topPaidEventNoCodes')}
          </Typography>
        </div>
      ) : (
        <>
          <Table
            className="codes-table"
            columns={columns}
            data={rows}
            page={0}
            rowsPerPage={rows.length}
            isRowClickable={false}
            hideFooter
          />
          <div className="code-cards">
            {rows.map((row, index) => (
              <div className="code-card" key={row.id}>
                <Typography variant="body-12-medium" className="code-index">
                  {index + 1}
                </Typography>
                <div className="code-row">
                  <Typography variant="body-12-regular" className="slate-text">
                    {t('topPaidEventRewardCodes')}
                  </Typography>
                  <CodeValue code={row.code} />
                </div>
                <div className="code-row">
                  <Typography variant="body-12-regular" className="slate-text">
                    {t('topPaidEventReceivedAt')}
                  </Typography>
                  <Typography variant="body-12-regular">
                    {row.receivedAtText}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </StyledRewardCodes>
  );
});

export default RewardCodeList;
